import { useEffect, useLayoutEffect, useRef } from 'react';
import { RESEND_AFTER_ONLINE_MS, RESEND_BACKOFF_MS } from '@constants/timing';

/**
 * A send that found no connection runs again by itself: `failures` counts the sends that failed that way in a row
 * (0 = nothing waiting). The next try comes after a growing pause (the `online` event is not reliable on every phone)
 * or shortly after the connection returns, whichever is first. `resend` must be safe to repeat (an idempotent send).
 */
export function useAutoResend(failures: number, resend: () => void) {
  const fn = useRef(resend);
  useLayoutEffect(() => {
    fn.current = resend;
  });
  useEffect(() => {
    if (!failures) return;
    let timer = 0;
    const schedule = (ms: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.current(), ms);
    };
    schedule(RESEND_BACKOFF_MS[Math.min(failures - 1, RESEND_BACKOFF_MS.length - 1)]);
    const back = () => schedule(RESEND_AFTER_ONLINE_MS);
    window.addEventListener('online', back);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('online', back);
    };
  }, [failures]);
}
